import React from 'react'
import { Table, useMediaSize } from '@ally/metronome-ui'
import {
  Account,
  AccountStatus,
  AccountType,
} from '@ally/data-types-afg-accounts'
import { sortBy } from 'lodash-es'
import AccountGroup from '../Shared/AccountGroup'
import AccountCard from '../Shared/AccountCard'
import AccountCardMbox from '../Shared/AccountCardMbox'
import { getColumns, getWarningMessage } from './TableColumns'
import { useCustomizeDashboard } from '../CustomizeDashboard/CustomizeDashboardContext/CustomizeDashboardContext'
import MobileCreditCardsList from '../Shared/MobileAccountsCard/MobileCreditCardsList'
import { NonPrintingWrapper } from '../Shared/NonPrintingWrapper'
import { useAccountTypes } from '../AccountTypeContext'

const ccStatusWeightLookup = (status: AccountStatus): number => {
  switch (status) {
    case AccountStatus.ACTIVE:
      return 1
    // TODO: verify status; for now all cards are ACTIVE
    // istanbul ignore next
    case AccountStatus.PAIDOFF:
      return 2
    // istanbul ignore next
    default:
      // istanbul ignore next
      return 0
  }
}

/**
 * Using lodash, sorts accounts by status and then accountNumber.
 */
function sortByStatusAndAccountNumber(accounts: Account[]): Account[] {
  return sortBy(accounts, [
    (account): number => ccStatusWeightLookup(account.status),
    (account): number => parseInt(account.accountNumber, 10),
  ])
}

type CCAccountType = 'PLATINUM' | 'EVERYDAY' | 'UNLIMITED'

const CreditCard: React.FC = () => {
  const [{ dashboardView, customizeApiData }] = useCustomizeDashboard()
  const { allCCAccounts } = useAccountTypes()

  const platinumAccounts = sortByStatusAndAccountNumber(
    allCCAccounts.filter(account => account.type === AccountType.PLATINUM),
  )
  const everydayAccounts = sortByStatusAndAccountNumber(
    allCCAccounts.filter(account => account.type === AccountType.EVERYDAY),
  )
  const unlimitedAccounts = sortByStatusAndAccountNumber(
    allCCAccounts.filter(account => account.type === AccountType.UNLIMITED),
  )

  const isMdDown = useMediaSize('MdDown')
  const isLgDown = useMediaSize('LgDown')
  const isPrint = window.matchMedia('print').matches

  const creditCardAccountCardInformation = {
    PLATINUM: {
      accounts: platinumAccounts,
      name: 'credit-cards-platinum',
      type: AccountType.PLATINUM,
      tableTypeHeader: 'Ally Platinum Credit Card' as const,
      captionText: 'Information about your Ally Platinum Credit Card accounts',
    },
    EVERYDAY: {
      accounts: everydayAccounts,
      name: 'credit-cards-everyday',
      type: AccountType.EVERYDAY,
      tableTypeHeader: 'Ally Everyday Cash Back Credit Card' as const,
      captionText:
        'Information about your Ally Everyday Cash Back Credit Card accounts',
    },
    UNLIMITED: {
      accounts: unlimitedAccounts,
      name: 'credit-cards-unlimited',
      type: AccountType.UNLIMITED,
      tableTypeHeader: 'Ally Unlimited Cash Back Credit Card' as const,
      captionText:
        'Information about your Ally Unlimited Cash Back Credit Card accounts',
    },
  }

  const ccAccountTypeOrder = customizeApiData.accountTypes
    .filter(account => account.lob === 'CC')
    .sort((a, b) => a.order - b.order)
  return (
    <>
      {ccAccountTypeOrder.map(({ name }) => {
        const value = creditCardAccountCardInformation[name as CCAccountType]

        const hideContentWell = value.accounts
          .map(cc => cc.status)
          .every(status =>
            [
              'BANKRUPT',
              'STOLEN',
              'LOST',
              'FROZEN_OR_FRAUD',
              'CLOSED',
            ].includes(status),
          )
        return value.accounts.length > 0 ? (
          <AccountGroup name={value.name}>
            <AccountCard type={value.type}>
              {isMdDown && !isPrint ? (
                <MobileCreditCardsList accounts={value.accounts} />
              ) : (
                <Table
                  data={value.accounts}
                  columns={getColumns({
                    tableTypeHeader: value.tableTypeHeader,
                    view: dashboardView,
                    isLgDown,
                    allCCAccounts: value.accounts,
                  })}
                  captionText={value.captionText}
                  fullWidth
                  suppressMdDownLayout
                  withFooter={!!getWarningMessage(value.accounts[0])}
                />
              )}
              <NonPrintingWrapper>
                {!hideContentWell && (
                  <AccountCardMbox
                    name={`ally-dashboard-${value.type.toLowerCase()}-card-well`}
                  />
                )}
              </NonPrintingWrapper>
            </AccountCard>
          </AccountGroup>
        ) : null
      })}
    </>
  )
}

export default CreditCard
